/**
 * Pure view helpers for the Internal Audit screens. Nothing here invents a value: an absent date, name or
 * percentage stays absent and the caller renders an explicit empty state (user ruling: no stubs).
 */

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Today's calendar date in India (IST) as YYYY-MM-DD. */
export function todayIstYmd(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(now);
}

/**
 * 'YYYY-MM-DD' -> 'DD-MM-YYYY'; a full ISO timestamp -> its IST calendar date as DD-MM-YYYY.
 * '', '—' and anything unparseable -> '' so the caller decides the empty wording.
 */
export function formatDdMmYyyy(value) {
  if (typeof value !== "string") return "";
  const v = value.trim();
  const m = YMD.exec(v);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (!/^\d{4}-\d{2}-\d{2}T/.test(v)) return "";
  const t = new Date(v);
  if (Number.isNaN(t.getTime())) return "";
  const [y, mo, d] = todayIstYmd(t).split("-");
  return `${d}-${mo}-${y}`;
}

/** Report metadata dates arrive as YYYY-MM-DD or the literal '—' when the plan has none. */
export function formatMetaDate(value) {
  return formatDdMmYyyy(value) || "-";
}

/** A CAP is overdue only when it is still open, has a real due date, and that date is before today (IST). */
export function isCapOverdue(row, now = new Date()) {
  if (!row || row.status === "Closed") return false;
  const due = typeof row.dueDate === "string" ? row.dueDate.trim() : "";
  if (!YMD.test(due)) return false;
  return due < todayIstYmd(now);
}

/** Coverage rows that actually carry a measurement (percent null = nothing answered yet). */
export function measuredCoverage(coverage) {
  return (Array.isArray(coverage) ? coverage : []).filter((r) => typeof r?.percent === "number");
}

/**
 * Whether the report may be exported: the executive summary and the auditor's opinion must both have been
 * written (or generated and kept) by a human. Returns the sections still missing.
 */
export function reportExportReadiness(snapshot) {
  const view = snapshot?.view;
  const missing = [];
  if (!String(view?.executiveSummaryText || "").trim()) missing.push("Executive summary");
  if (!String(view?.affirmationQuote || "").trim()) missing.push("Auditor's opinion");
  return { ready: !!view && missing.length === 0, missing };
}

/**
 * Stage states for the Workflow tab, derived from what is actually stored on the internal-audit package.
 * 'agreement' has no record in the system, so it is 'untracked' rather than assumed done.
 * Returns { states: {stageKey: 'done'|'current'|'pending'|'untracked'}, currentKey: string|null }.
 */
export function deriveIaStageStates(pkg, stageKeys) {
  const questions = pkg?.questionnaire?.questions || [];
  const findings = pkg?.auditFindings?.rows || [];
  const done = {
    plan_creation: pkg?.planWorkflow === "circulated",
    questionnaire_scheduling: questions.length > 0,
    audit_conduct: findings.length > 0 && findings.every((r) => r.type),
    review_signoff: pkg?.auditReport?.signOffStatus === "approved",
  };
  const states = {};
  let currentKey = null;
  for (const key of stageKeys) {
    if (!(key in done)) {
      states[key] = "untracked";
    } else if (done[key]) {
      states[key] = "done";
    } else if (currentKey === null) {
      currentKey = key;
      states[key] = "current";
    } else {
      states[key] = "pending";
    }
  }
  return { states, currentKey };
}
